import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardBody, CardSubtitle, CardTitle } from "reactstrap";
import DateRangeSelector from "./DateRangeSelector";
import { getReservationAnalytics } from "../../slices/EVDashboard/ReservationAnalytics/thunk";
import {
  formatDateForPayload,
  getCurrentWeekRange,
} from "../Utils/UtilityFunctions";

const HeatmapSkeleton = ({ cellSize, cellWidth, cellGap, dayLabelWidth }) => {
  const skeletonDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const skeletonHours = Array(24).fill(0);

  return (
    <div style={{ overflowX: "auto", height: "260px" }}>
      {/* Hour labels skeleton */}
      <div className="d-flex mb-1" style={{ marginLeft: `${dayLabelWidth}px` }}>
        {skeletonHours.map((_, index) => {
          const showLabel = index % 3 === 0;
          return (
            <div
              key={index}
              style={{
                width: `${cellWidth}px`,
                marginRight: `${cellGap}px`,
                textAlign: "center",
                fontSize: "10px",
                flexShrink: 0,
              }}
            >
              {showLabel && (
                <div
                  style={{
                    width: "12px",
                    height: "10px",
                    backgroundColor: "#e0e0e0",
                    borderRadius: "2px",
                    margin: "0 auto",
                    animation: "pulse 1.5s ease-in-out infinite",
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Heatmap grid skeleton */}
      {skeletonDays.map((day, dayIndex) => (
        <div key={dayIndex} className="d-flex align-items-center mb-1">
          <div
            style={{
              width: `${dayLabelWidth}px`,
              fontSize: "13px",
              textAlign: "left",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: "30px",
                height: "13px",
                backgroundColor: "#e0e0e0",
                borderRadius: "3px",
                animation: "pulse 1.5s ease-in-out infinite",
              }}
            />
          </div>
          <div className="d-flex" style={{ gap: `${cellGap}px` }}>
            {skeletonHours.map((_, hourIndex) => (
              <div
                key={hourIndex}
                style={{
                  width: `${cellWidth}px`,
                  height: `${cellSize}px`,
                  backgroundColor: "#e0e0e0",
                  borderRadius: "3px",
                  flexShrink: 0,
                  animation: "pulse 1.5s ease-in-out infinite",
                  animationDelay: `${(dayIndex * 24 + hourIndex) * 0.01}s`,
                }}
              />
            ))}
          </div>
        </div>
      ))}

      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
};

const ReservationHeatmap = ({ trailingMonths = 3 }) => {
  const [dateRange, setDateRange] = useState(getCurrentWeekRange());
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const reservationTrend = useSelector(
    (state) => state.ReservationAnalytics.reservationData
  );
  const dailyHeatmap = reservationTrend?.dailyHeatmap || [];

  // Calculate max reservations for normalization
  const maxReservations = Math.max(
    ...dailyHeatmap.flatMap((day) => day.hours.map((h) => h.peakReservations)),
    1 // Avoid division by zero
  );

  // Get color based on occupancy level
  const getOccupancyColor = (reservations) => {
    const ratio = reservations / maxReservations;

    if (ratio === 0) return "#CAD5E2"; // Low
    if (ratio <= 0.4) return "#90A1B9"; // 20-40%
    if (ratio <= 0.6) return "#FFB86A"; // 40-60%
    if (ratio <= 0.8) return "#FF6900"; // 60-80%
    return "#F54900"; // High
  };

  const handleDateRangeChange = (dateRangeData) => {
    const { startDate, endDate, type } = dateRangeData;
    const dateRange = {
      period: type,
      from: formatDateForPayload(startDate),
      to: formatDateForPayload(endDate),
    };
    setDateRange(dateRange);
  };

  useEffect(() => {
    if (dateRange) {
      dispatch(getReservationAnalytics(setLoading, dateRange));
    }
  }, [dateRange]);

  // Responsive cell size calculation
  const cellSize = 24;
  const cellWidth = 28;
  const cellGap = 2;
  const dayLabelWidth = 40;

  return (
    <Card style={{ border: "1px solid #0000001A", borderRadius: "10px" }}>
      <CardBody className="p-4">
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start mb-4">
          <div>
            <CardTitle
              tag="h3"
              className="mb-2"
              style={{
                fontSize: "18px",
                color: "#0A0A0A",
                fontWeight: "400",
              }}
            >
              Peak Reservation Hours
            </CardTitle>
          </div>
          <div className="flex-shrink-0">
            <DateRangeSelector
              onDateRangeChange={handleDateRangeChange}
              isShowDropdown={false}
              oneWeekMode={true}
            />
          </div>
        </div>

        {loading ? (
          <HeatmapSkeleton
            cellSize={cellSize}
            cellWidth={cellWidth}
            cellGap={cellGap}
            dayLabelWidth={dayLabelWidth}
          />
        ) : (
          <div style={{ overflowX: "auto", height: "260px" }}>
            {/* Hour labels */}
            <div
              className="d-flex mb-1"
              style={{ marginLeft: `${dayLabelWidth}px` }}
            >
              {dailyHeatmap[0]?.hours.map((hourData, index) => {
                const hour = parseInt(hourData.hour.split(":")[0]);
                const showLabel = hour % 3 === 0;
                return (
                  <div
                    key={index}
                    className="text-muted"
                    style={{
                      width: `${cellWidth}px`,
                      marginRight: `${cellGap}px`,
                      textAlign: "center",
                      fontSize: "10px",
                      fontWeight: "500",
                      flexShrink: 0,
                    }}
                  >
                    {showLabel ? hour : ""}
                  </div>
                );
              })}
            </div>

            {/* Heatmap grid */}
            {dailyHeatmap.map((dayData) => (
              <div key={dayData.day} className="d-flex align-items-center mb-1">
                <div
                  className="text-muted fw-medium"
                  style={{
                    width: `${dayLabelWidth}px`,
                    fontSize: "13px",
                    textAlign: "left",
                    flexShrink: 0,
                  }}
                >
                  {dayData.day}
                </div>
                <div className="d-flex " style={{ gap: `${cellGap}px` }}>
                  {dayData.hours.map((hourData, index) => (
                    <div
                      key={index}
                      style={{
                        width: `${cellWidth}px`,
                        height: `${cellSize}px`,
                        backgroundColor: getOccupancyColor(
                          hourData.peakReservations
                        ),
                        borderRadius: "3px",
                        cursor: "pointer",
                        transition: "transform 0.2s, box-shadow 0.2s",
                        flexShrink: 0,
                      }}
                      title={`${dayData.day} ${hourData.hour}: ${hourData.peakReservations} reservations`}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.15)";
                        e.currentTarget.style.boxShadow =
                          "0 2px 8px rgba(0,0,0,0.2)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Legend */}
        <div
          className="d-flex align-items-center mb-3 mt-2 flex-wrap"
          style={{ gap: "12px", fontSize: "12px" }}
        >
          <span className="text-muted fw-medium">Occupancy:</span>
          <div className="d-flex align-items-center">
            <div
              style={{
                width: "18px",
                height: "18px",
                backgroundColor: "#CAD5E2",
                borderRadius: "3px",
                marginRight: "5px",
              }}
            />
            <span>Low</span>
          </div>
          <div className="d-flex align-items-center">
            <div
              style={{
                width: "18px",
                height: "18px",
                backgroundColor: "#90A1B9",
                borderRadius: "3px",
                marginRight: "5px",
              }}
            />
            <span>20-40%</span>
          </div>
          <div className="d-flex align-items-center">
            <div
              style={{
                width: "18px",
                height: "18px",
                backgroundColor: "#FFB86A",
                borderRadius: "3px",
                marginRight: "5px",
              }}
            />
            <span>40-60%</span>
          </div>
          <div className="d-flex align-items-center">
            <div
              style={{
                width: "18px",
                height: "18px",
                backgroundColor: "#FF6900",
                borderRadius: "3px",
                marginRight: "5px",
              }}
            />
            <span>60-80%</span>
          </div>
          <div className="d-flex align-items-center">
            <div
              style={{
                width: "18px",
                height: "18px",
                backgroundColor: "#F54900",
                borderRadius: "3px",
                marginRight: "5px",
              }}
            />
            <span>High</span>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default ReservationHeatmap;
